import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import { Alert, AlertActionCloseButton, PageSection } from '@patternfly/react-core';
import axios from "@app/Security/AxiosAuthentication";
import AlertModel from '@app/Model/AlertModel';
import ApiEndpoints from "@app/utils/ApiEndpoints";
import htmlParser from '@app/utils/htmlParser';
import RollbarHelper from '@app/utils/RollbarHelper';

interface PageAlertProps {
    pageId: number;
}

const PageAlerts: React.FC<PageAlertProps> = ({ pageId, children }) => {
    const { userState } = React.useContext(UserContext);
    const dismissedAlertsStorageKey = `nda_dismissed_page_alerts_${userState.user.id}`;

    // All alerts which might be shown on this page for this user & FPU
    const [alerts, setAlerts] = React.useState<AlertModel[]>([]);

    // Alerts that were dismissed before this pageview
    const initialDismissedAlertsJson: string | null = localStorage.getItem(dismissedAlertsStorageKey);
    const initialDismissedAlerts: number[] = initialDismissedAlertsJson
        ? JSON.parse(initialDismissedAlertsJson)
        : [];
    // Alerts that were dismissed by the user this pageview
    const [userDismissedAlerts, setUserDismissedAlerts] = React.useState<number[]>([]);
    // Alerts that are automatically dismissed after this pageview
    const [pageDismissedAlerts, setPageDismissedAlerts] = React.useState<number[]>([]);

    // When an alert is dismissed by loading the page or by the user, update our stored dismissed alert ids
    React.useEffect(() => {
        const updated_dismissed_alerts: number[] = [
            ...initialDismissedAlerts,
            ...userDismissedAlerts,
            ...pageDismissedAlerts
        ];
        localStorage.setItem(
            dismissedAlertsStorageKey,
            JSON.stringify([...new Set(updated_dismissed_alerts)])
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDismissedAlerts, pageDismissedAlerts]);

    // When we get a list of alerts, set the one-time ones to dismissed immediately (if not already dismissed)
    React.useEffect(() => {
        const updated_dismissed_alerts: number[] = [...pageDismissedAlerts];
        alerts
            .filter(alert => alert.recurrence === 'once' && !initialDismissedAlerts.includes(alert.id))
            .forEach(alert => updated_dismissed_alerts.push(alert.id));
        if (updated_dismissed_alerts.length > pageDismissedAlerts.length) {
            setPageDismissedAlerts(updated_dismissed_alerts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alerts]);

    // When the page, user, or FPU change, fetch new alerts
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        if (userState.user?.currentFpu.producerId > 0 && pageId > 0) {
            const baseUrl: string = process.env.API_BASEURL ?? "";
            const endPoint: string = ApiEndpoints.ALERTS_COLLECTION;
            const fpu: number = userState.user.currentFpuId;
            const state: string = userState.user.currentFpu.state;
            const county = `${userState.user.currentFpu.county}, ${state}`;
            const district: string = userState.user.currentFpu.voteDist;
            const query = `?fpu_id=${fpu}&page_id=${pageId}&state=${state}&county=${county}&district=${district}`;
            const url = baseUrl + endPoint + query;
            axios.get(url, {
                withCredentials: true,
                signal: controller.signal,
                headers: {
                    "Accept": "application/json"
                }
            })
                .then(response => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const data: any = response.data;
                    const alertModels = data.map(alertPost => new AlertModel(alertPost));
                    if (mounted) {
                        setAlerts(alertModels);
                    }
                })
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .catch(error => {

                    RollbarHelper.logRequestError(error, "get alerts");
                    
                    if (mounted) {
                        setAlerts([]);
                    }
                });
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId, userState.user.id, userState.user.currentFpu]);

    /**
     * Event handler function for a user manually dismissing an alert
     * @param id Alert id to dismiss
     */
    const handleUserDismissedAlert = (id: number) => {
        const updated_dismissed_alerts: number[] = [...userDismissedAlerts, id];
        setUserDismissedAlerts(updated_dismissed_alerts);
    };

    const alertsToDisplay = alerts
        // Filter out previously-dismissed alerts and ones that the user dismissed on this pageview
        .filter(
            alert => !initialDismissedAlerts.includes(alert.id) && !userDismissedAlerts.includes(alert.id)
        );

    return (
        <>
            {alertsToDisplay.length > 0 &&
                <PageSection>
                    {alertsToDisplay
                        .map(alert =>
                            <Alert
                                key={alert.id}
                                title={alert.title}
                                className="pf-u-mb-md"
                                role="alert"
                                actionClose={
                                    alert.recurrence === 'dismissable'
                                        ? <AlertActionCloseButton onClose={() => handleUserDismissedAlert(alert.id)} />
                                        : null
                                }
                            >
                                <div className="pf-c-content">
                                    {htmlParser.htmlToJSX(alert.details ?? '')}
                                </div>
                            </Alert>
                        )}
                </PageSection>
            }
            {children}
        </>
    );
};

export default PageAlerts;